import React, { useEffect, useRef, useState } from "react";
import MagicPageHeader from "../components/common/MagicPageHeader";
import Grid from "../components/mui/Grid";
import Box from "../components/mui/Box";
import Cards from "../components/leaderboard/Cards";
import LeaderboardTable from "../components/leaderboard/LeaderboardTable";
import WestOutlinedIcon from "@mui/icons-material/WestOutlined";
import Stack from "../components/mui/Stack";
import LeaderoardTableMUI from "../components/leaderboard/LeaderoardTableMUI";
import {
  useGetLeaderboardListQuery,
  useLazyGetLeaderboardByIdQuery,
} from "../store/apis/leaderboardApi";
import { RootState } from "../store";
import { useSelector } from "react-redux";
import Button from "../components/mui/Button";
import Loader from "../components/common/Loader";
import Typography from "../components/mui/Typography";
import usePermission from "../helper/usePermission";
import { acl } from "../helper/userAccessConstant";

const LeaderboardContainer = () => {
  const [showTable, setShowTable] = useState(false);
  const [activeTier, setActiveTier] = useState("");
  const [centerCount, setCenterCount] = useState<any>({});
  const selector = useSelector((state: RootState) => state?.user?.filter);

  const [params, setParams] = useState<any>({
    programMasterId: selector?.selectedProgram?.value,
    quarterMasterId: selector?.selectedQuarter?.quarterMasterId,
    ProgramYearId: selector?.selectedYear?.value,
  }); // State to manage API params

  const [pagination, setPagination] = useState<any>({
    pageNo: "1",
    pageSize: "10",
    programMasterId: selector?.selectedProgram?.value,
    quarterMasterId: selector?.selectedQuarter?.quarterMasterId,
    ProgramYearId: selector?.selectedYear?.value,
  }); // State to manage API params

  const [awardId, setAwardId] = useState<any>();
  const [tableData, setTableData] = useState<any[]>([]); 

  const {
    data: leaderboardResponse,
    isLoading: isGettingLeaderboard,
    refetch: refreshLeaderboard,
  } = useGetLeaderboardListQuery(params);

  const [
    trigger,
    {
      data: getLeaderboardById,
      isLoading: isGettingLeaderboardByID,
      refetch: refetchLeaderboardById,
    },
  ] = useLazyGetLeaderboardByIdQuery();

  const { permissions = {}, isAdmin } = usePermission();
  const leaderboardAccess = permissions[acl.leaderboard.module];

  // Handle selector changes: Update params and clear the table data
  useEffect(() => {
    setParams({
      programMasterId: selector?.selectedProgram?.value,
      quarterMasterId: selector?.selectedQuarter?.quarterMasterId,
      ProgramYearId: selector?.selectedYear?.value,
    });

    setPagination({
      pageNo: "1",
      pageSize: "10",
      programMasterId: selector?.selectedProgram?.value,
      quarterMasterId: selector?.selectedQuarter?.quarterMasterId,
      ProgramYearId: selector?.selectedYear?.value,
    });

    setTableData([]); // Clear table data on selector change
    refreshLeaderboard(); // Refetch leaderboard data
  }, [selector]);

  // Update table data and UI when leaderboardResponse changes
  useEffect(() => {
    if (leaderboardResponse && leaderboardResponse.totalAwards !== 0) {
      const updatedCentresCount = leaderboardResponse.leaderboard?.filter(
        (award: any) => award?.awardId === awardId 
      );

      setTableData(getLeaderboardById?.leaderboard || []); 
      
      handleButtonClick(
        activeTier,
        awardId,
        updatedCentresCount?.[0]?.centers || []
      );
    } else {
      handleBack();
    }
  }, [leaderboardResponse, getLeaderboardById]);

  const handleButtonClick = async (
    tier: string,
    awardId: number,
    centres: any
  ) => {
    if (!awardId) {
      return;
    }
    setCenterCount(centres);
    setTableData(getLeaderboardById?.leaderboard || []); 

    try {
      await trigger({ id: awardId, params: pagination });
      setActiveTier(tier);
      setShowTable(true);
      setAwardId(awardId);
    } catch (error) {
      console.log(error);
    }
  };

  const handleBack = () => {
    setShowTable(false);
    setAwardId("");
    setTableData([]); // Clear table data on back
  };

  const onPageChange = async (event: any, page: number) => {
    try {
     
    const updatedPagination = {
      ...pagination, // Include existing parameters
      pageSize: 10,  // Always set the page size
      pageNo: page,  // Update to the new page number
    };

    setPagination(updatedPagination); // Update local state
      await trigger({ id: awardId, params: updatedPagination  });
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <>
      {showTable ? (
        <Box>
          <MagicPageHeader
            title={
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  textAlign: "center",
                  gap: "5px",
                }}
              >
                <WestOutlinedIcon
                  onClick={handleBack}
                  sx={{ cursor: "pointer" }}
                />
                <Box sx={{ fontWeight: "600", fontSize: "16px" }}>
                  {activeTier}
                </Box>
              </Box>
            }
            subTitle={`Explore the Performance List of ${activeTier} Card`}
            chipLabel=""
          />
          <LeaderoardTableMUI
            rowData={tableData} 
            handlePaginationChange={onPageChange}
            activePaginationPageNo={Number(pagination.pageNo) || 1}
            isLoading={isGettingLeaderboardByID}
            centreCount={centerCount}
          />
        </Box>
      ) : leaderboardAccess?.module?.canRead ? (
        <Box>
          <MagicPageHeader
            title="Leaderboard"
            subTitle="Here you can view the list of top centres with their performance."
            chipLabel=""
          />

          <Box sx={{ flexGrow: 1, padding: 2 }}>
            <Grid container spacing={3}>
              {isGettingLeaderboard || isGettingLeaderboardByID ? (
                <Loader loading={true} />
              ) : leaderboardResponse?.totalAwards !== 0 ? (
                leaderboardResponse?.leaderboard?.map((compound: any) => {
                  return (
                    <Grid item xs={12} md={4} key={compound.tier}>
                      <Cards
                        tier={compound?.awardName?.toUpperCase()}
                        data={compound?.centers}
                        getTierColor={""}
                        handleButtonClick={() => {
                          handleButtonClick(
                            compound?.awardName,
                            compound?.awardId,
                            compound?.centers
                          );
                        }}
                      />
                    </Grid>
                  );
                })
              ) : (
                <Typography
                  sx={{
                    textAlign: "center",
                    color: "grey",
                    margin: "0 auto",
                  }}
                >
                  No awards found for the provided filters.
                </Typography>
              )}
            </Grid>
          </Box>
        </Box>
      ) : (
        <Box sx={{ textAlign: "center", padding: 2 }}>
          <Typography
            sx={{
              fontSize: "1rem",
              fontWeight: "bold",
              color: "#ff4444",
              borderRadius: "10px",
              display: "inline-block",
            }}
          >
            You don't have access
          </Typography>
        </Box>
      )}
    </>
  );
};

export default LeaderboardContainer;
